/**
 * The map toolbar's dropdown, and the group that keeps them exclusive.
 *
 * One implementation for every group in the bar — the filters and the layers —
 * because a second hand-rolled dropdown is how two controls in the same product
 * end up behaving differently: one closing on Escape and one not, which is
 * exactly what the legacy toolbar's mix of buttons and selects was.
 *
 * Disclosures, not `role="menu"`. A menu promises arrow-key navigation between
 * its items; these panels hold ordinary buttons and checkboxes that Tab already
 * reaches, and announcing a keyboard model that is not implemented is worse than
 * announcing none.
 *
 * Exclusivity lives in the group rather than in each dropdown: the triggers sit
 * side by side and two open panels would overlap.
 */

import type { ReactNode } from "react";
import { createContext, useContext, useEffect, useId, useRef, useState } from "react";

interface Group {
  openId: string | null;
  setOpenId: (id: string | null) => void;
}

const GroupContext = createContext<Group>({ openId: null, setOpenId: () => {} });

interface GroupProps {
  className?: string;
  children: ReactNode;
}

export function MenuGroup({ className, children }: GroupProps) {
  const [openId, setOpenId] = useState<string | null>(null);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Close on an outside click. Pointerdown rather than click, matching the
  // Combobox: the panel is gone before a click elsewhere lands, so dragging the
  // map does not leave one hanging over the buildings.
  useEffect(() => {
    function onPointerDown(event: PointerEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpenId(null);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, []);

  return (
    <GroupContext.Provider value={{ openId, setOpenId }}>
      <div className={className} ref={wrapRef}>{children}</div>
    </GroupContext.Provider>
  );
}

/** Close whatever is open. For a caller whose state made the panel meaningless. */
export function useCloseMenus(): () => void {
  const { setOpenId } = useContext(GroupContext);
  return () => setOpenId(null);
}

interface Props {
  /** Unique within the group; only the identity matters. */
  id: string;
  label: string;
  /** The group's current selection, read off the closed trigger. */
  value: string;
  /** True when the group is doing something to the map, which tints the trigger. */
  active: boolean;
  disabled?: boolean;
  /** Clears this group alone. Omit for a group with nothing to clear. */
  onReset?: () => void;
  children: ReactNode;
}

export function Dropdown({
  id, label, value, active, disabled, onReset, children,
}: Props) {
  const { openId, setOpenId } = useContext(GroupContext);
  const open = openId === id;
  const panelId = useId();
  const labelId = useId();
  const triggerRef = useRef<HTMLButtonElement>(null);

  return (
    <div
      className="filter-menu"
      onKeyDown={(event) => {
        if (event.key !== "Escape" || !open) return;
        setOpenId(null);
        // Focus goes back to the trigger, not to the document: a keyboard user
        // who presses Escape inside the panel would otherwise be returned to the
        // top of the page and have to tab past the whole bar again.
        triggerRef.current?.focus();
      }}
    >
      <button
        type="button"
        ref={triggerRef}
        className={active ? "menu-btn on" : "menu-btn"}
        aria-expanded={open}
        aria-controls={panelId}
        disabled={disabled}
        onClick={() => setOpenId(open ? null : id)}
      >
        <span className="menu-label" id={labelId}>{label}</span>
        <span className="menu-value">{value}</span>
        <span className="menu-caret" aria-hidden="true">▾</span>
      </button>

      {open && (
        // Named by the trigger's own label, so the panel announces as the group
        // the user just opened rather than as an anonymous set of controls.
        <div
          className="menu-panel"
          id={panelId}
          role="group"
          aria-labelledby={labelId}
        >
          {children}

          {/* Per group, because clearing one is the common case: a reader who
              has narrowed to F–G and then picked a deadline usually wants the
              deadline back off, not the letters back on. The bar's own button
              still clears everything at once. */}
          {onReset && (
            <button
              type="button"
              className="menu-reset"
              // Shortened on screen, named in full for a screen reader, which
              // hears these buttons out of their visual context. The visible
              // text is contained in the accessible name (WCAG 2.5.3).
              aria-label={`Limpiar ${label.toLowerCase()}`}
              disabled={!active}
              onClick={onReset}
            >
              Limpiar
            </button>
          )}
        </div>
      )}
    </div>
  );
}
